import { colorTextAction } from '@calendly/ui/theme'

import { applyFixedMeetingsOrganizerScope } from 'features/meetings/hooks/search_data/supergraph_meeting_search_utils'
import { INVALID_SUPERGRAPH_SCOPE_TYPES } from 'features/meetings/supergraph/scope_utils'
import { mapNodeToMeeting } from 'features/meetings/utils'
import { mapScopeTypeToBackendValue } from 'shared/api/meetings/utils'

/**
 * Maps search results sort dropdown → SuperGraph `orderBy.start_time` (no BEST_MATCH / relevance in SuperGraph).
 */
export const mapSearchSortToStartTimeOrder = (sortBy) => {
  if (sortBy === 'LEAST_RECENT') return 'ASC'
  return 'DESC'
}

/**
 * Build `Meetings_MeetingsInput` for the dedicated keyword search page from `useToPrepareSearchFiltersParams`
 * output plus route scope. SuperGraph exposes a single `search_keyword` filter (no separate transcript/title
 * `searchIn` dimensions like legacy `meetings_search`).
 */
export const buildMeetingsSearchSupergraphInput = ({
  query,
  sortBy,
  preparedFilter,
  scopeType,
  scopeId,
}) => {
  const filter = {}

  const q = typeof query === 'string' ? query.trim() : ''
  if (q) filter.search_keyword = q

  if (preparedFilter?.inviteeEmails?.length) {
    filter.invitee_emails = preparedFilter.inviteeEmails
  }
  if (preparedFilter?.eventTypeUuids?.length) {
    filter.event_type_uuids = preparedFilter.eventTypeUuids
  }
  if (preparedFilter?.kinds?.length) {
    filter.kinds = preparedFilter.kinds
  }
  if (preparedFilter?.role?.length) {
    filter.role = preparedFilter.role
  }

  if (preparedFilter?.statuses?.length === 1) {
    filter.status = preparedFilter.statuses[0]
  } else if (preparedFilter?.statuses?.length > 1) {
    filter.statuses = preparedFilter.statuses
  }

  if (preparedFilter?.startTimeRange) {
    const { operation, start, end } = preparedFilter.startTimeRange
    filter.start_time = { operation, start, end }
  }

  if (preparedFilter?.userIds?.length) {
    filter.user_uuids = preparedFilter.userIds
  }
  if (preparedFilter?.teamIds?.length) {
    filter.team_uuids = preparedFilter.teamIds
  }

  const mappedScope = mapScopeTypeToBackendValue(scopeType, scopeId)

  if (mappedScope === 'all_users_and_teams') {
    if (scopeId) filter.scope_id = scopeId
  } else if (mappedScope && !INVALID_SUPERGRAPH_SCOPE_TYPES.has(mappedScope)) {
    filter.scope_type = mappedScope
    if (scopeId && mappedScope !== 'my_calendly' && scopeId !== 'me') {
      filter.scope_id = scopeId
    }
  }

  applyFixedMeetingsOrganizerScope(filter)

  return {
    filter,
    pagination: { first: 100, after: null },
    orderBy: { start_time: mapSearchSortToStartTimeOrder(sortBy) },
  }
}

/**
 * Map SuperGraph `meetings` connection → the same card/list shape as {@link convertSearchResponse} in `utils.js`.
 * No highlight/passages (legacy `meetings_search` only).
 */
export const convertSupergraphSearchResponse = (response, timezone, hasMsTeamsFlag = false) => {
  if (!response?.meetings?.edges) return null

  const { meetings } = response
  const data = meetings.edges.map(({ node }) => {
    const mapped = mapNodeToMeeting(node, timezone, hasMsTeamsFlag)
    return {
      ...mapped,
      color: mapped.eventType?.color || colorTextAction,
      attendees: (mapped.attendees || []).filter(({ kind }) => kind === 'INVITEE'),
      guests: (mapped.attendees || []).filter(({ kind }) => kind === 'EXTERNAL'),
      transcriptMatches: [],
      hasMeetingIntelligence: Boolean(mapped.meetingIntelligence?.uuid),
      trackedMeetingUuid: mapped.meetingIntelligence?.uuid,
    }
  })

  return {
    totalSize: meetings.total,
    data,
  }
}
